import { Box, keyframes, styled } from '@mui/material';

import StarLargeIcon from 'assets/icons/StarLarge.svg';
import StarSmallIcon from 'assets/icons/StarSmall.svg';

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/**
 * The two stars trade places: one is at full weight while the other is dimmed, and they swap
 * over the first half of the loop before holding for the second.
 *
 * Written as two keyframe sets rather than one played at an offset, because the phases are not a
 * clean half-cycle apart — the hold at the end means a `animation-delay` of half the loop would
 * park the small star mid-fade on first paint.
 */
const smallStarTwinkle = keyframes`
    0%     { opacity: 1;    transform: scale(1.1); }
    12.73% { opacity: 0.75; transform: scale(0.975); }
    25.45% { opacity: 0.5;  transform: scale(0.85); }
    38.18% { opacity: 0.75; transform: scale(0.975); }
    50.91% { opacity: 1;    transform: scale(1.1); }
    100%   { opacity: 1;    transform: scale(1.1); }
`;

const largeStarTwinkle = keyframes`
    0%     { opacity: 0.5;  transform: scale(0.85); }
    12.73% { opacity: 0.75; transform: scale(0.975); }
    25.45% { opacity: 1;    transform: scale(1.1); }
    38.18% { opacity: 0.75; transform: scale(0.975); }
    50.91% { opacity: 0.5;  transform: scale(0.85); }
    100%   { opacity: 0.5;  transform: scale(0.85); }
`;

const SparkleFrame = styled(Box)({
    position: 'relative',
    width: 16,
    height: 16,
    flexShrink: 0,
});

/**
 * Both stars share everything but their keyframes, their box and where they sit, so the animation
 * is the only thing either one has to name.
 */
const Star = styled('img')({
    position: 'absolute',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    [REDUCED_MOTION]: {
        animation: 'none',
    },
});

const SmallStar = styled(Star)({
    left: 9.83,
    top: 0.5,
    width: 5.667,
    height: 5.667,
    animationName: smallStarTwinkle,
});

const LargeStar = styled(Star)({
    left: 1.17,
    top: 3.83,
    width: 11,
    height: 11,
    animationName: largeStarTwinkle,
});

interface TwinklingSparkleProps {
    /**
     * How long one full twinkle takes, in milliseconds.
     *
     * Required rather than defaulted: this mark is meant to read as one phase of whatever motion
     * it sits inside, so the caller owning that timeline is the only thing that knows the number.
     * A default here would be a second copy of it, free to drift out of step.
     */
    readonly loopMs: number;
}

/**
 * A 16px sparkle whose two stars twinkle in opposite phase.
 *
 * Two `img`s rather than one glyph because the stars pulse against each other — a single asset
 * could only fade as a unit, which is the one thing this mark is not doing.
 *
 * Decorative, so it is hidden from assistive tech; whatever it sits beside carries the meaning.
 */
export default function TwinklingSparkle({ loopMs }: Readonly<TwinklingSparkleProps>) {
    const animationDuration = `${loopMs}ms`;

    return (
        <SparkleFrame aria-hidden>
            <SmallStar src={StarSmallIcon} alt="" style={{ animationDuration }} />
            <LargeStar src={StarLargeIcon} alt="" style={{ animationDuration }} />
        </SparkleFrame>
    );
}
